import { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, Archive, ArrowCounterClockwise, Image as ImageIcon, X, SpinnerGap, Calendar, MapPin, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_EVENT = {
  title: "", subtitle: "", category: "Masterclass", description: "", date: "", time: "",
  location: "Leingarten", price_info: "", booking_url: "", button_text: "Jetzt anfragen",
  images: [], status: "active", sort_order: 0, spots_total: "", spots_left: "", highlight: "", is_free: false,
};

const STATUS_LABELS = { active: "Aktiv", draft: "Entwurf", archived: "Archiviert" };
const STATUS_STYLES = {
  active: "bg-green-100 text-green-700",
  draft: "bg-yellow-100 text-yellow-700",
  archived: "bg-gray-200 text-gray-600",
};

const imgSrc = (url, apiUrl) => (url?.startsWith("http") ? url : `${apiUrl}${url}`);

const formatDate = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }) : "Datum offen";

const inputCls = "w-full px-3 py-2.5 border border-[#E6E2D6] rounded-lg text-sm focus:outline-none focus:border-[#D4AF37] bg-white";

export const EventsManager = ({ apiUrl, authFetch }) => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [today, setToday] = useState("");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${apiUrl}/api/admin/events`);
      const data = await res.json();
      setEvents(data.events || []);
      setCategories(data.categories || []);
      setToday(data.today || "");
    } catch {
      toast.error("Events konnten nicht geladen werden");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (ev, status) => {
    const res = await authFetch(`${apiUrl}/api/admin/events/${ev.id}/status?status=${status}`, { method: "PATCH" });
    if (res.ok) {
      setEvents(events.map(e => (e.id === ev.id ? { ...e, status } : e)));
      toast.success(status === "archived" ? "Event archiviert" : "Event aktiviert");
    } else toast.error("Fehler beim Aktualisieren");
  };

  const remove = async (ev) => {
    if (!window.confirm(`„${ev.title}" endgültig löschen?`)) return;
    const res = await authFetch(`${apiUrl}/api/admin/events/${ev.id}`, { method: "DELETE" });
    if (res.ok) {
      setEvents(events.filter(e => e.id !== ev.id));
      toast.success("Event gelöscht");
    } else toast.error("Fehler beim Löschen");
  };

  const onSaved = (saved, isNew) => {
    setEvents(isNew ? [saved, ...events] : events.map(e => (e.id === saved.id ? saved : e)));
    setEditing(null);
  };

  const visible = events.filter(e => filter === "all" || e.status === filter);
  const counts = events.reduce((acc, e) => ({ ...acc, [e.status]: (acc[e.status] || 0) + 1 }), {});

  return (
    <div className="space-y-4" data-testid="events-manager">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {[["all", "Alle"], ["active", "Aktiv"], ["draft", "Entwürfe"], ["archived", "Archiviert"]].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              data-testid={`events-filter-${key}`}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                filter === key ? "bg-[#2C2C2C] text-white border-[#2C2C2C]" : "bg-white border-[#E6E2D6] text-[#2C2C2C] hover:bg-[#F9F7F2]"
              }`}
            >
              {label} {key !== "all" && counts[key] ? `(${counts[key]})` : ""}
            </button>
          ))}
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY_EVENT })}
          data-testid="event-create-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-white text-sm rounded-full hover:bg-[#2C2C2C] transition-colors"
        >
          <Plus size={16} weight="bold" /> Neues Event
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><SpinnerGap size={32} className="animate-spin text-[#D4AF37]" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-[#E6E2D6] text-center">
          <Calendar size={40} className="mx-auto text-[#E6E2D6] mb-4" />
          <p className="text-[#2C2C2C]/60">Keine Events in dieser Ansicht</p>
        </div>
      ) : (
        visible.map(ev => (
          <EventRow key={ev.id} ev={ev} apiUrl={apiUrl} today={today} onEdit={() => setEditing(ev)} onStatus={setStatus} onDelete={remove} />
        ))
      )}

      {editing && (
        <EventForm
          initial={editing}
          categories={categories}
          apiUrl={apiUrl}
          authFetch={authFetch}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

const EventRow = ({ ev, apiUrl, today, onEdit, onStatus, onDelete }) => {
  const isPast = ev.date && today && ev.date < today;
  return (
    <div className="bg-white p-4 sm:p-5 rounded-xl border border-[#E6E2D6] flex flex-col sm:flex-row gap-4" data-testid={`event-row-${ev.id}`}>
      <div className="w-full sm:w-28 h-24 sm:h-20 flex-shrink-0 bg-[#F9F7F2] rounded-lg overflow-hidden flex items-center justify-center">
        {ev.images?.[0] ? (
          <img src={imgSrc(ev.images[0], apiUrl)} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon size={28} className="text-[#E6E2D6]" />
        )}
      </div>
      <div className="flex-grow min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-xs uppercase tracking-wider text-[#D4AF37]">{ev.category}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[ev.status]}`}>{STATUS_LABELS[ev.status]}</span>
          {isPast && ev.status === "active" && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">Vergangen – wird nicht angezeigt</span>
          )}
        </div>
        <p className="font-medium text-lg leading-tight truncate">{ev.title}</p>
        {ev.subtitle && <p className="text-sm text-[#2C2C2C]/60 truncate">{ev.subtitle}</p>}
        <div className="flex flex-wrap gap-3 text-xs text-[#2C2C2C]/60 mt-2">
          <span className="flex items-center gap-1"><Calendar size={13} /> {formatDate(ev.date)}</span>
          {ev.time && <span className="flex items-center gap-1"><Clock size={13} /> {ev.time}</span>}
          {ev.location && <span className="flex items-center gap-1"><MapPin size={13} /> {ev.location}</span>}
          {typeof ev.spots_left === "number" && <span className="text-[#D4AF37] font-medium">{ev.spots_left} Plätze frei</span>}
        </div>
      </div>
      <div className="flex sm:flex-col gap-1 justify-end sm:justify-start flex-shrink-0">
        <button onClick={onEdit} title="Bearbeiten" data-testid={`event-edit-${ev.id}`} className="p-2 text-[#9A8C73] hover:text-[#2C2C2C] hover:bg-[#F9F7F2] rounded-lg transition-colors"><PencilSimple size={18} /></button>
        {ev.status === "archived" ? (
          <button onClick={() => onStatus(ev, "active")} title="Wieder aktivieren" data-testid={`event-activate-${ev.id}`} className="p-2 text-[#9A8C73] hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"><ArrowCounterClockwise size={18} /></button>
        ) : (
          <button onClick={() => onStatus(ev, "archived")} title="Archivieren" data-testid={`event-archive-${ev.id}`} className="p-2 text-[#9A8C73] hover:text-[#2C2C2C] hover:bg-[#F9F7F2] rounded-lg transition-colors"><Archive size={18} /></button>
        )}
        <button onClick={() => onDelete(ev)} title="Löschen" data-testid={`event-delete-${ev.id}`} className="p-2 text-[#9A8C73] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash size={18} /></button>
      </div>
    </div>
  );
};

const EventForm = ({ initial, categories, apiUrl, authFetch, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...EMPTY_EVENT, ...initial, images: initial.images || [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isNew = !initial.id;

  const set = (name, value) => setForm(prev => ({ ...prev, [name]: value }));
  const onChange = (e) => set(e.target.name, e.target.value);

  const uploadFiles = async (files) => {
    const remaining = 3 - form.images.length;
    if (remaining <= 0) return toast.error("Maximal 3 Bilder pro Event");
    setUploading(true);
    const added = [];
    for (const file of Array.from(files).slice(0, remaining)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await authFetch(`${apiUrl}/api/admin/events/upload`, { method: "POST", body: fd });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Upload fehlgeschlagen");
        added.push(data.url);
      } catch (err) {
        toast.error(err.message || "Upload fehlgeschlagen");
      }
    }
    if (added.length) setForm(prev => ({ ...prev, images: [...prev.images, ...added] }));
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Bitte einen Titel eingeben");
    setSaving(true);
    const { id, created_at, updated_at, ...payload } = form;
    payload.sort_order = Number(payload.sort_order) || 0;
    payload.spots_total = payload.spots_total === "" || payload.spots_total === null ? null : Number(payload.spots_total);
    payload.spots_left = payload.spots_left === "" || payload.spots_left === null ? null : Number(payload.spots_left);
    payload.highlight = payload.highlight || null;
    try {
      const res = await authFetch(`${apiUrl}/api/admin/events${isNew ? "" : `/${id}`}`, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Speichern fehlgeschlagen");
      toast.success(isNew ? "Event angelegt" : "Event gespeichert");
      onSaved(data, isNew);
    } catch (err) {
      toast.error(err.message || "Speichern fehlgeschlagen");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="event-form"
        className="bg-white rounded-2xl w-full max-w-2xl my-4 p-6 sm:p-8 space-y-5 max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
            {isNew ? "Neues Event" : "Event bearbeiten"}
          </h2>
          <button type="button" onClick={onClose} data-testid="event-form-close" className="p-2 hover:bg-[#F9F7F2] rounded-lg"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Titel *</label>
            <input name="title" value={form.title} onChange={onChange} required className={inputCls} data-testid="event-title-input" placeholder="z.B. The Ultimate Blond & Bronde" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Untertitel / Artists</label>
            <input name="subtitle" value={form.subtitle || ""} onChange={onChange} className={inputCls} data-testid="event-subtitle-input" placeholder="z.B. April Yardley x Jennifer Vallentin" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Kategorie</label>
            <select name="category" value={form.category} onChange={onChange} className={inputCls} data-testid="event-category-select">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Status</label>
            <select name="status" value={form.status} onChange={onChange} className={inputCls} data-testid="event-status-select">
              <option value="active">Aktiv (sichtbar)</option>
              <option value="draft">Entwurf (unsichtbar)</option>
              <option value="archived">Archiviert</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Datum</label>
            <input type="date" name="date" value={form.date || ""} onChange={onChange} className={inputCls} data-testid="event-date-input" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Uhrzeit</label>
            <input name="time" value={form.time || ""} onChange={onChange} className={inputCls} data-testid="event-time-input" placeholder="z.B. 10–16 Uhr" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Ort</label>
            <input name="location" value={form.location || ""} onChange={onChange} className={inputCls} data-testid="event-location-input" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Preis-Hinweis</label>
            <input name="price_info" value={form.price_info || ""} onChange={onChange} className={inputCls} data-testid="event-price-input" placeholder="z.B. ab 499 € netto · Begrenzte Plätze" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Beschreibung</label>
            <textarea name="description" value={form.description || ""} onChange={onChange} rows={4} className={`${inputCls} resize-none`} data-testid="event-description-input" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Buchungslink (Eventbrite, mailto:, …)</label>
            <input name="booking_url" value={form.booking_url || ""} onChange={onChange} className={inputCls} data-testid="event-booking-input" placeholder="https://www.eventbrite.com/…" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Button-Text</label>
            <input name="button_text" value={form.button_text || ""} onChange={onChange} className={inputCls} data-testid="event-button-text-input" placeholder="Tickets / Jetzt anfragen" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Reihenfolge (0 = ganz oben)</label>
            <input type="number" name="sort_order" value={form.sort_order} onChange={onChange} className={inputCls} data-testid="event-sort-input" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Plätze gesamt</label>
            <input type="number" min="0" name="spots_total" value={form.spots_total ?? ""} onChange={onChange} className={inputCls} data-testid="event-spots-total-input" placeholder="z.B. 10" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Freie Plätze (leer = nicht anzeigen)</label>
            <input type="number" min="0" name="spots_left" value={form.spots_left ?? ""} onChange={onChange} className={inputCls} data-testid="event-spots-left-input" placeholder="z.B. 4" />
          </div>
          <div>
            <label className="text-xs text-[#2C2C2C]/60 mb-1 block">Badge-Text (z.B. „Letzte Plätze", „Neu")</label>
            <input name="highlight" value={form.highlight || ""} onChange={onChange} className={inputCls} data-testid="event-highlight-input" placeholder="automatisch bei ≤ 5 freien Plätzen" />
          </div>
          <label className="flex items-center gap-3 mt-6 cursor-pointer text-sm">
            <input type="checkbox" checked={!!form.is_free} onChange={(e) => set("is_free", e.target.checked)} className="accent-[#D4AF37]" data-testid="event-free-checkbox" />
            Kostenloses Community-Event
          </label>
        </div>

        <div>
          <label className="text-xs text-[#2C2C2C]/60 mb-2 block">Fotos (max. 3)</label>
          <div className="flex flex-wrap gap-3">
            {form.images.map((img, i) => (
              <div key={img} className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#E6E2D6]">
                <img src={imgSrc(img, apiUrl)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => set("images", form.images.filter((_, idx) => idx !== i))}
                  data-testid={`event-image-remove-${i}`}
                  className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-red-500"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            ))}
            {form.images.length < 3 && (
              <label className="w-24 h-24 rounded-lg border-2 border-dashed border-[#E6E2D6] flex flex-col items-center justify-center text-[#9A8C73] hover:border-[#D4AF37] hover:text-[#D4AF37] cursor-pointer transition-colors text-xs">
                {uploading ? <SpinnerGap size={20} className="animate-spin" /> : <><ImageIcon size={22} /><span className="mt-1">Upload</span></>}
                <input type="file" accept="image/*" multiple className="hidden" data-testid="event-image-upload" disabled={uploading}
                  onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
              </label>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm rounded-full border border-[#E6E2D6] hover:bg-[#F9F7F2]">Abbrechen</button>
          <button type="submit" disabled={saving || uploading} data-testid="event-save-btn"
            className="px-6 py-2.5 text-sm rounded-full bg-[#2C2C2C] text-white hover:bg-[#D4AF37] transition-colors disabled:opacity-50">
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </form>
    </div>
  );
};
